// ACTIONS → Add extension(s): the one extension dialog for the aircraft tabs that
// carry limitations (Components, Modifications, Inspections). Moved here from
// src/aircraft/ComponentWithSelected.tsx (2026-10-09) so the inspections module,
// which must not import from src/aircraft/, uses the same dialog.
//
// Legacy (Legacy code/htdocs; intent, not verified behaviour): one modal for every
// limitation type, mod_aircraft/js/aircraft_edit.js:1374-1468 — Extension "/ unit",
// Reason, Permissioned By, and on a single line "(Allowed: n unit)" in red, shown
// and never enforced — saved by includes/aircraft_insp_update.inc:603-685.
//
// Figma: no verified Aeronet Design 5.0 node for this dialog; it uses the MUI
// Dialog as the other aircraft ACTIONS dialogs do. Visual parity not checked.
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { axios } from "../api";

export interface ExtensionApprover {
  uid: number;
  gecos: string | null;
}

/** What a single ticked line already carries; legacy's single modal opens on it. */
export interface ExtensionInitial {
  extension?: string;
  reason?: string;
  approver?: number;
}

/** A failed request's message for a snackbar: the server's detail, else a plain line. */
export function requestErrorDetail(e: any): string {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (e?.response?.status === 403) return "You do not have permission to do that.";
  return "The request failed. Try again.";
}

const plural = (n: number) => `${n} limitation${n === 1 ? "" : "s"}`;

export const AddExtensionsDialog: React.FC<{
  open: boolean;
  aircraftId: number;
  limitationIds: number[];
  onClose: () => void;
  onDone: (message: string) => void;
  onError: (message: string) => void;
  /**
   * Where the extension is saved, and the ids' key — the Components tab's route
   * by default. The Modifications tab (H104) and the Inspections tab send the
   * same values to PUT /aircraft/{id}/modifications|inspections/extensions with
   * `log_ids`: legacy's multi Add Extensions is one dialog for all of them.
   */
  savePath?: string;
  idsKey?: "limitation_ids" | "log_ids";
  title?: string;
  /** The unit the value is counted in (days on a date axis, else the axis); shown and sent. */
  unit?: string;
  /** Legacy's "Allowed: n unit" for a single line (display only, as legacy). */
  allowed?: string;
  /** The Inspections tab requires a reason (2026-10-09); legacy accepted a blank one. */
  reasonRequired?: boolean;
  initial?: ExtensionInitial;
}> = ({
  open,
  aircraftId,
  limitationIds,
  onClose,
  onDone,
  onError,
  savePath,
  idsKey = "limitation_ids",
  title = "Add extensions",
  unit,
  allowed,
  reasonRequired = false,
  initial,
}) => {
  const [approvers, setApprovers] = useState<ExtensionApprover[]>([]);
  const [extension, setExtension] = useState("");
  const [reason, setReason] = useState("");
  const [approver, setApprover] = useState<number | "">("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setExtension(initial?.extension ?? "");
    setReason(initial?.reason ?? "");
    setApprover(initial?.approver ?? "");
    const id = `components-extension-approvers-${aircraftId}`;
    axios
      .get(`/v6/aircraft/${aircraftId}/components/extension-approvers`, { id })
      .then((r: any) => setApprovers(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setApprovers([]))
      .finally(() => axios.storage?.remove?.(id));
    // initial is read when the dialog opens, not on every parent render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, aircraftId]);

  const valid =
    extension.trim() !== "" &&
    !Number.isNaN(Number(extension)) &&
    approvers.some((a) => a.uid === approver) &&
    (!reasonRequired || reason.trim() !== "");

  const save = async () => {
    setSaving(true);
    try {
      const r: any = await axios.put(savePath ?? `/v6/aircraft/${aircraftId}/components/extensions`, {
        [idsKey]: limitationIds,
        extension: extension.trim(),
        extension_reason: reason.trim(),
        extension_permissioned_by: approver,
        ...(unit ? { unit } : {}),
      });
      const n = r?.data?.updated?.length ?? 0;
      onDone(n ? `Extension saved on ${plural(n)}` : "No change: those limitations already carry this extension");
    } catch (e) {
      onError(`Extension NOT saved. ${requestErrorDetail(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const counted = unit
    ? `counted in ${unit}`
    : "units on an hours/cycles limitation, days on a date limitation";

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="add-extensions-title" fullWidth maxWidth="sm">
      <DialogTitle id="add-extensions-title">{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {allowed ? (
            <Typography variant="body2" color="error" data-testid="extension-allowed">
              Allowed: {allowed}
            </Typography>
          ) : null}
          <TextField
            label="Extension"
            required
            value={extension}
            onChange={(e) => setExtension(e.target.value)}
            helperText={`Replaces the current extension on ${plural(limitationIds.length)}; ${counted}.`}
            inputProps={{ inputMode: "decimal" }}
            InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
          />
          <TextField
            label="Reason"
            required={reasonRequired}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
          />
          <FormControl required>
            <InputLabel id="extension-approver-label">Permissioned by</InputLabel>
            <Select
              labelId="extension-approver-label"
              label="Permissioned by"
              value={approvers.some((a) => a.uid === approver) ? approver : ""}
              onChange={(e) => setApprover(e.target.value === "" ? "" : Number(e.target.value))}
            >
              {approvers.map((a) => (
                <MenuItem key={a.uid} value={a.uid}>
                  {a.gecos || `User ${a.uid}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" disabled={!valid || saving} onClick={save} sx={{ textTransform: "none" }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExtensionsDialog;
